/*
 * Add-to-cart confirmation pop-up.
 *
 * Plugs into product-form.js the same way <cart-notification> does: product-form
 * appends the ids from getSectionsToRender() to the /cart/add.js request, then
 * calls setActiveElement() and renderContents() with the parsed response.
 * Instead of redirecting to the cart page we open a responsive pop-up that
 * confirms the item, offers an upsell and lets the shopper keep browsing.
 */
class CartConfirmation extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector('#cart-confirmation');
    this.overlay = this.querySelector('.cart-confirmation__overlay');
    this.productContainer = this.querySelector('#cart-confirmation-product');
    this.upsell = this.querySelector('#cart-confirmation-upsell');
    this.upsellList = this.querySelector('.cart-confirmation-upsell__list');

    this.onBodyClick = this.handleBodyClick.bind(this);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelectorAll('[data-cart-confirmation-close]').forEach((button) =>
      button.addEventListener('click', this.close.bind(this))
    );
    // Dismiss when clicking the backdrop (anywhere in the pop-up layer that is
    // not inside the dialog panel).
    this.addEventListener('click', (evt) => {
      if (!evt.target.closest('#cart-confirmation')) this.close();
    });
    // Add a suggested product straight from the pop-up (delegated so it survives
    // the upsell list being re-rendered on every add). The trigger lives inside
    // #cart-confirmation, so the dismiss handler above never fires for it.
    this.addEventListener('click', (evt) => {
      const trigger = evt.target.closest('[data-kb-cart-add]');
      if (!trigger) return;
      if (trigger.dataset.variantId) {
        evt.preventDefault();
        this.addSuggestion(trigger);
      }
      // No variant id → it is an <a> to the product page; let it navigate.
    });
    this.dialog.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.addedProductId = parsedState.product_id;

    // On the cart page itself the item appears in the cart list (cart.js re-renders
    // it on cartUpdate), so skip the pop-up and confirm on the clicked button.
    if (document.getElementById('main-cart-items')) {
      this.refreshBubble(parsedState);
      this.flashAddedButton(parsedState.variant_id);
      return;
    }

    // Render the freshly added line item.
    const productHTML = this.getSectionInnerHTML(
      parsedState.sections['cart-notification-product'],
      `[id="cart-notification-product-${this.cartItemKey}"]`
    );
    this.productContainer.innerHTML = productHTML;

    this.refreshBubble(parsedState);

    // When re-rendering an already-open pop-up (adding a suggestion), replay the
    // success check-mark so it reads as a fresh confirmation.
    if (this.classList.contains('active')) this.replayCheckmark();

    this.loadUpsell();
    this.open();
  }

  // Refresh the header cart bubble count.
  refreshBubble(parsedState) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      bubble.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
    }
  }

  // Briefly swap the quick-add button label to "added" for the given variant.
  flashAddedButton(variantId) {
    const input = document.querySelector(`product-form input[name="id"][value="${variantId}"]`);
    const button = input?.closest('form')?.querySelector('button[name="add"]');
    const label = button?.querySelector('.quick-add__label:not(.sold-out-message)');
    if (!button || !label || !window.cartStrings?.added) return;

    clearTimeout(button.addedTimer);
    if (!button.dataset.originalLabel) button.dataset.originalLabel = label.textContent;
    label.textContent = window.cartStrings.added;
    button.classList.add('quick-add__submit--added');
    button.addedTimer = setTimeout(() => {
      label.textContent = button.dataset.originalLabel;
      button.classList.remove('quick-add__submit--added');
    }, 2000);
  }

  replayCheckmark() {
    if (this.reduceMotion) return;
    const nodes = this.dialog.querySelectorAll(
      '.cart-confirmation__check-circle, .cart-confirmation__check-mark'
    );
    nodes.forEach((node) => {
      node.style.animation = 'none';
      // Force reflow so the animation restarts from the beginning.
      void node.offsetWidth;
      node.style.animation = '';
    });
  }

  isUpsellEnabledForViewport() {
    const width = window.innerWidth;
    let flag;
    if (width <= 749) flag = this.dataset.upsellMobile;
    else if (width <= 989) flag = this.dataset.upsellTablet;
    else flag = this.dataset.upsellDesktop;
    // Undefined (setting not present) defaults to enabled; only an explicit
    // "false" turns the suggestions off for this device tier.
    return flag !== 'false';
  }

  loadUpsell() {
    if (!this.upsell) return;

    // Respect the per-device enable/disable setting — skip the pinned card and
    // the network fetch entirely when suggestions are off for this viewport.
    if (!this.isUpsellEnabledForViewport()) {
      this.hideUpsell();
      return;
    }

    const pinnedId = this.upsell.dataset.pinnedId;
    const hasPinnedCard = this.upsellList && this.upsellList.children.length > 0;

    // Prefer the server-rendered pinned product, unless it is the item just
    // added (in which case fall back to related recommendations).
    if (hasPinnedCard && pinnedId && String(pinnedId) !== String(this.addedProductId)) {
      this.showUpsell();
      return;
    }

    this.fetchRelatedUpsell();
  }

  fetchRelatedUpsell() {
    const url = this.dataset.upsellUrl;
    if (!url || !this.addedProductId) {
      this.hideUpsell();
      return;
    }

    fetch(`${url}&product_id=${this.addedProductId}`)
      .then((response) => response.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const cards = Array.from(doc.querySelectorAll('.cart-confirmation-upsell__list > li, .product-grid > li'))
          .filter((li) => String(li.dataset.productId) !== String(this.addedProductId))
          .slice(0, 2);

        if (!cards.length) {
          this.hideUpsell();
          return;
        }

        this.upsellList.innerHTML = '';
        cards.forEach((card) => {
          const item = document.createElement('li');
          item.className = 'cart-confirmation-upsell__item';
          item.innerHTML = card.innerHTML;
          this.upsellList.appendChild(item);
        });
        this.showUpsell();
      })
      .catch((error) => {
        console.error('cart-confirmation upsell failed', error);
        this.hideUpsell();
      });
  }

  showUpsell() {
    if (this.upsell) this.upsell.hidden = false;
  }

  hideUpsell() {
    if (this.upsell) this.upsell.hidden = true;
  }

  // Add a suggested product via AJAX, then re-render the whole pop-up for the
  // newly added item (mirrors the product-form.js add pattern).
  addSuggestion(trigger) {
    const variantId = trigger.dataset.variantId;
    if (!variantId) {
      if (trigger.dataset.productUrl) window.location = trigger.dataset.productUrl;
      return;
    }
    if (trigger.classList.contains('loading')) return;

    trigger.classList.add('loading');
    trigger.setAttribute('disabled', 'disabled');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('sections', this.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) return;
        this.renderContents(response);
      })
      .catch((error) => {
        console.error('cart-confirmation add suggestion failed', error);
      })
      .finally(() => {
        trigger.classList.remove('loading');
        trigger.removeAttribute('disabled');
      });
  }

  open() {
    document.body.classList.add('cart-confirmation-open');
    this.classList.add('active');
    if (!this.reduceMotion) this.dialog.classList.add('animate');

    this.dialog.addEventListener(
      'transitionend',
      () => {
        this.dialog.focus();
        trapFocus(this.dialog);
      },
      { once: true }
    );
    // Fallback focus if there is no transition (e.g. reduced motion).
    if (this.reduceMotion) {
      this.dialog.focus();
      trapFocus(this.dialog);
    }

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.classList.remove('active');
    this.dialog.classList.remove('animate');
    document.body.classList.remove('cart-confirmation-open');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this && !target.closest('cart-confirmation') && this.classList.contains('active')) {
      this.close();
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-confirmation', CartConfirmation);
